import React from "react";
import PropTypes from "prop-types";
import { observable } from "mobx";
import { observer } from "mobx-react";
import isFunction from "lodash/isFunction";
import isUndefined from "lodash/isUndefined";
import isNull from "lodash/isNull";
import autobind from "autobind-decorator";
import pick from "lodash/pick";
import clientErrorLogger from "util/clientErrorLogger";
import isEmpty from "lodash/isEmpty";
import isArray from "lodash/isArray";
import extend from "lodash/extend";
import forEach from "lodash/forEach";
import trim from "lodash/trim";
import classNames from "classnames";
import CustomError from "gradifui/components/form/CustomError";
import { CustomFormContext } from "../components/form/CustomForm";

// superclass must be a subclass of React.Component and must not implement the lifecycle methods defined below
const FormField = (superclass) => observer(class extends superclass {
    static contextType = CustomFormContext;

    static propTypes = extend({}, {
        fieldName: PropTypes.string.isRequired,
        id: PropTypes.string,
        name: PropTypes.string,
        // inConverter: convert storage value into input value
        inConverter: PropTypes.func,
        // outConverter: format input value to storage
        outConverter: PropTypes.func,
        // blurConverter: format the output of inConverter for blurred display (note: this differs from FormFieldMixin)
        blurConverter: PropTypes.func,
        // event hooks
        onChange: PropTypes.func,
        onFocus: PropTypes.func,
        onBlur: PropTypes.func,
        overrideForm: PropTypes.object,
        showErrors: PropTypes.bool,
        autoComplete: PropTypes.oneOf(["on", "off"])
    }, superclass.propTypes);

    static defaultProps = extend({}, {
        // Since all these values are going to end up on inputs, we need to coerce
        // them to strings in order to compare the backing model value against the
        // rendered element value (this.inputElement, set via setInputRef)
        inConverter(value) {
            return ((value || value === 0) && value.toString) ? trim(value.toString()) : "";
        },
        outConverter(value) {
            return (value && typeof value.trim === "function") ? trim(value) : value;
        },
        blurConverter(value) {
            return value;
        },
        showErrors: true,
        autoComplete: "on"
    }, superclass.defaultProps);

    @observable focused;
    @observable value;

    constructor() {
        super();
        this.focused = false;
        this.value = "";
    }

    componentDidUpdate(prevProps) {
        // Check for changes to props that could impact the backing value, namely inConverters and outConverters
        const backingValue = this.formModel.get(prevProps.fieldName);
        if (backingValue !== this.value && (prevProps.inConverter !== this.props.inConverter || prevProps.outConverter !== this.props.outConverter)) {
            this.value = this.readBackingValue(this.props, true);
        }
    }

    componentDidMount() {
        this.value = this.readBackingValue(this.props);
        // If using the back/forward browser buttons the inputs may be pre-filled, so we need to
        // sync up the data as soon as possible. Well, not quite as soon as possible: we need to let
        // the parent's componentDidMount get called first so that all its listeners will be added.
        this._watchForSilentChangesTimeout = setTimeout(this.checkForUncontrolledChanges, 0);

        let formEmitter = this.formModel.emitter;
        if (formEmitter) {
            formEmitter.on("WATCH_FOR_UNCONTROLLED_CHANGES", this.setSilentChangeWatcherTimeout);
            formEmitter.on("STOP_WATCHING_FOR_UNCONTROLLED_CHANGES", this.clearSilentChangeWatcherTimeout);
            // ensure that backing values are up to date before the form is submitted
            formEmitter.on("PRE_SUBMIT", this.updateBackingValue.bind(this, true));
            formEmitter.on("UPDATED_MODEL_VALUE", e => {
                if (e.data.fieldName === this.props.fieldName) {
                    this.value = this.readBackingValue(this.props, true);
                }
            });
        }

        if (isFunction(this.didMount)) {
            this.didMount();
        }
    }

    componentWillUnmount() {
        this.clearSilentChangeWatcherTimeout();

        let formEmitter = this.formModel.emitter;
        if (formEmitter) {
            formEmitter.off("WATCH_FOR_UNCONTROLLED_CHANGES", this.setSilentChangeWatcherTimeout);
            formEmitter.off("STOP_WATCHING_FOR_UNCONTROLLED_CHANGES", this.clearSilentChangeWatcherTimeout);
            formEmitter.off("PRE_SUBMIT");
            formEmitter.off("UPDATED_MODEL_VALUE");
        }
    }

    get formModel() {
        return !isUndefined(this.props.overrideForm) ? this.props.overrideForm : this.context.form;
    }

    // callback style ref, preferable to named refs
    @autobind
    setInputRef(input) {
        this.inputElement = input;
    }

    // list props that should be passed to "native" html input
    @autobind
    getInnerInputProps() {
        const propNames = ["placeholder", "disabled", "autoComplete"];
        const innerProps = pick(this.props, propNames);

        // for accessibility, if there's no label, try to use the placeholder text instead
        if (!this.props.label && this.props.placeholder) {
            innerProps["aria-label"] = this.props.placeholder;
        }

        return innerProps;
    }

    @autobind
    checkForUncontrolledChanges() {
        if (this.inputElement && this.props.outConverter(this.inputElement.value) !== this.value) {
            try {
                this.inputElement.dispatchEvent(new Event("change"));
            } catch (ex) {
                // IE (as of IE11) still doesn't support the Event constructor syntax
                const event = document.createEvent("Event");
                event.initEvent("change", true, true);
                this.inputElement.dispatchEvent(event);
            }
        }
    }

    @autobind
    readValue() {
        const useObservable = this.focused || this.value === "" || !isEmpty(this.getErrors());
        return useObservable ? this.value : this.props.blurConverter(this.value);
    }

    @autobind
    readBackingValue(props, skipErrors) {
        let value = this.formModel.data.get(props.fieldName);
        if (!isEmpty(this.getErrors()) && skipErrors) {
            // use observable value if the backing value is invalid
            return this.value;
        }

        return (isUndefined(value) || isNull(value)) ? "" : props.inConverter(value);
    }

    @autobind
    updateBackingValue(shouldValidate) {
        let value = this.value && typeof this.value.trim === "function" ? trim(this.value) : this.value;
        value = value === "" ? null : value;
        value = this.props.outConverter(value);

        const fieldName = this.props.fieldName;
        this.formModel.data.set(fieldName, value);
    }

    @autobind
    handleChange(e) {
        if (!this.inputElement) {
            clientErrorLogger.notify("No input element defined for form field", "error");
            return;
        }

        this.value = this.inputElement.value;
        // The input may already be changed at this point, but this makes sure that it is also validated.
        this.updateBackingValue();

        if (isFunction(this.props.onChange)) {
            this.props.onChange(e);
        }
    }

    @autobind
    handleFocus(e) {
        this.focused = true;
        this.formModel.emitter && this.formModel.emitter.emit("WATCH_FOR_UNCONTROLLED_CHANGES");

        if (isFunction(this.props.onFocus)) {
            this.props.onFocus(e);
        }
    }

    @autobind
    handleBlur(e) {
        this.formModel.emitter && this.formModel.emitter.emit("STOP_WATCHING_FOR_UNCONTROLLED_CHANGES");
        this.focused = false;
        this.formModel.markFieldDone(this.props.fieldName);

        if (isFunction(this.props.onBlur)) {
            this.props.onBlur(e);
        }
    }

    @autobind
    // This timer is set on focus and reset on change.
    setSilentChangeWatcherTimeout() {
        this.clearSilentChangeWatcherTimeout();
        // The actual changeTimeout will be used for the focused input.
        if (!this.focused) {
            this._watchForSilentChangesTimeout = setTimeout(function() {
                this.checkForUncontrolledChanges();
                this.setSilentChangeWatcherTimeout();
            }.bind(this), 300);
        }
    }

    @autobind
    clearSilentChangeWatcherTimeout() {
        if (this._watchForSilentChangesTimeout) {
            clearTimeout(this._watchForSilentChangesTimeout);
            this._watchForSilentChangesTimeout = undefined;
        }
    }

    @autobind
    getErrors() {
        return this.formModel.errors.get(this.props.fieldName);
    }

    @autobind
    renderErrors() {
        return (
            <div className="error-message-container">
                {this.props.showErrors &&
                    <CustomError errors={this.getErrors()} />
                }
            </div>
        );
    }

    @autobind
    getClassNames(additionalClassNames) {
        let classes = {
            "input-container": true,
            "has-errors": this.props.showErrors && this.getErrors(),
            disabled: this.props.disabled
        };
        if (this.props.className) {
            classes[this.props.className] = true;
        }
        if (isArray(additionalClassNames)) {
            forEach(additionalClassNames, name => {
                classes[name] = true;
            });
            return classNames(classes);
        } else if (typeof additionalClassNames === "object") {
            return classNames(extend(classes, additionalClassNames));
        } else {
            return classNames(classes) + " " + additionalClassNames;
        }
    }
});

export default FormField;
